import { Injectable } from '@angular/core';
import { Platform } from '@ionic/angular';
import { UserRole } from '../enums/user-role.enum';

// type UserRoleString = 'FARM_ADMIN' | 'VIVENT_ADMIN' | 'DISTRIBUTOR' | 'ADMIN' | '';

interface MenuItem {
  title: string;
  url: string;
  icon: string;
  roles: UserRole[];
  platforms?: ('web' | 'android' | 'ios')[];
  menuType?: ('menuItems' | 'adminPages' | 'account')[];
}

@Injectable({ providedIn: 'root' })
export class RolePermissionService {
  private currentRole: UserRole | null = null;
  constructor(private platform: Platform) {}

  private allMenuItems: MenuItem[] = [
    {
      title: 'pages.menuItems.dashboard',
      url: '/dashboard',
      icon: 'grid',
      roles: [UserRole.FARM_ADMIN, UserRole.VIVENT_ADMIN, UserRole.DISTRIBUTOR, UserRole.BASIC],
      platforms: [],
      menuType: []
    },
    {
      title: 'pages.menuItems.distributors',
      url: '/distributors',
      icon: 'repeat',
      roles: [UserRole.VIVENT_ADMIN],
      platforms: ['web', 'android', 'ios'],
      menuType: ['adminPages']
    },
    {
      title: 'pages.menuItems.endclients',
      url: '/endclients',
      icon: 'business',
      roles: [UserRole.VIVENT_ADMIN, UserRole.DISTRIBUTOR],
      platforms: ['web', 'android', 'ios'],
      menuType: ['adminPages']
    },
    {
      title: 'pages.menuItems.projects',
      url: '/projects',
      icon: 'folder',
      roles: [UserRole.FARM_ADMIN, UserRole.VIVENT_ADMIN, UserRole.DISTRIBUTOR, UserRole.BASIC],
      platforms: ['web', 'android', 'ios'],
      menuType: ['adminPages'],
    },
    {
      title: 'pages.menuItems.trials',
      url: '/trials',
      icon: 'flask',
      roles: [UserRole.FARM_ADMIN, UserRole.VIVENT_ADMIN, UserRole.DISTRIBUTOR, UserRole.BASIC],
      platforms: ['web', 'android', 'ios'],
      menuType: ['adminPages']
    },
    // Hidden web entry for VIVENT_ADMIN+DISTRIBUTOR
    {
      title: 'pages.menuItems.trials',
      url: '/trials',
      icon: 'flask',
      roles: [UserRole.VIVENT_ADMIN, UserRole.DISTRIBUTOR],
      platforms: ['web'],
      menuType: []
    },
    {
      title: 'pages.menuItems.devices',
      url: '/devices',
      icon: 'phone-portrait',
      roles: [UserRole.FARM_ADMIN, UserRole.BASIC],
      platforms: ['web'],
      menuType: ['adminPages']
    },
    {
      title: 'pages.menuItems.devices',
      url: '/devices-provision',
      icon: 'cube',
      roles: [UserRole.VIVENT_ADMIN],
      platforms: ['web','android','ios'],
      menuType: ['adminPages']
    },
    {
      title: 'pages.menuItems.bluetooth',
      url: '/bluetooth',
      icon: 'bluetooth',
      roles: [],
      platforms: [],
      menuType: ['adminPages']
    },
    {
      title: 'pages.menuItems.deviceInformation',
      url: '/device-info-component',
      icon: 'information-circle',
      roles: [],
      platforms: [],
      menuType: ['adminPages']
    },
    {
      title: 'pages.menuItems.devices',
      url: '/devices-earmark',
      icon: 'link',
      roles: [UserRole.DISTRIBUTOR],
      platforms: ['web','android','ios'],
      menuType: ['adminPages']
    },
    {
      title: 'pages.menuItems.users',
      url: '/users',
      icon: 'people',
      roles: [UserRole.VIVENT_ADMIN, UserRole.FARM_ADMIN, UserRole.DISTRIBUTOR],
      platforms: ['web'],
      menuType: ['adminPages']
    },
    {
      title: 'pages.menuItems.notifications',
      url: '/notifications',
      icon: 'notifications',
      roles: [],
      platforms: ['web'],
      menuType: ['adminPages']
    },
    {
      title: 'pages.menuItems.account',
      url: '/account',
      icon: 'person',
      roles: [UserRole.VIVENT_ADMIN, UserRole.FARM_ADMIN, UserRole.DISTRIBUTOR, UserRole.BASIC],
      platforms: ['web', 'ios', 'android'],
      menuType: ['account']
    },
    {
      title: 'pages.menuItems.switchAccount',
      url: '/switch-account',
      icon: 'swap-horizontal',
      roles: [UserRole.VIVENT_ADMIN],
      platforms: ['web', 'ios', 'android'],
      menuType: ['account']
    },
    {
      title: 'pages.menuItems.guidanceInformation',
      url: '/guidance-information',
      icon: 'information-circle',
      roles: [UserRole.VIVENT_ADMIN, UserRole.FARM_ADMIN, UserRole.DISTRIBUTOR, UserRole.BASIC],
      platforms: ['ios', 'android'],
      menuType: ['account']
    }
  ];

  setCurrentRole(role: UserRole) {
    this.currentRole = role;
    localStorage.setItem('userRole', role);
  }

  getCurrentRole(): UserRole | null {
    const storedRole = localStorage.getItem('userRole') as UserRole;
    return this.currentRole || (Object.values(UserRole).includes(storedRole) ? storedRole : null);
  }

  getFilteredMenuItems(
    platformType: 'web' | 'android' | 'ios',
    menuType: 'menuItems' | 'adminPages' | 'account'
  ): MenuItem[] {
    if (!this.currentRole) {return [];}
    return this.allMenuItems.filter(item => {
      // Check role access
      const hasRoleAccess = this.currentRole ? item.roles.includes(this.currentRole) : false;

      // Check platform restriction if specified
      const hasPlatformAccess = !item.platforms || item.platforms.includes(platformType);

      const hasMenuTypeAccess = !item.menuType || item.menuType.includes(menuType);

      return hasRoleAccess && hasPlatformAccess && hasMenuTypeAccess;
    }).map(item => {
      // Inject dynamic title for Devices
      if (item.url === '/devices') {
        return {
          ...item,
          title: (this.currentRole === UserRole.DISTRIBUTOR || this.currentRole === UserRole.VIVENT_ADMIN)
            ? 'pages.menuItems.devices'
            : 'pages.menuItems.assignedDevices'
        };
      }
      return item;
    });
  }

  // Check if a specific route is allowed for current role considering platform
  isRouteAllowed(url: string): boolean {
    if (!this.currentRole) {return false;}

    const platformType = this.getPlatformType();
    const candidates = this.allMenuItems.filter(item => item.url === url)
      .filter(item => !item.platforms || item.platforms.includes(platformType));

    if (candidates.length === 0) {return false;}
    return candidates.some(item => item.roles.includes(this.currentRole));
  }

  private getPlatformType(): 'web' | 'android' | 'ios' {
    if (this.platform.is('ios')) {return 'ios';}
    if (this.platform.is('android')) {return 'android';}
    return 'web';
  }

  // Check if an action is allowed (for buttons)
  isActionAllowed(action: string): boolean {
    const actionPermissions: Record<string, UserRole[]> = {
      'edit_device': [UserRole.DISTRIBUTOR, UserRole.FARM_ADMIN, UserRole.BASIC],
      'create_project': [UserRole.FARM_ADMIN, UserRole.DISTRIBUTOR],
      'edit_project': [UserRole.FARM_ADMIN, UserRole.DISTRIBUTOR],
      'create_trial': [UserRole.FARM_ADMIN, UserRole.DISTRIBUTOR],
      'edit_trial': [UserRole.FARM_ADMIN, UserRole.DISTRIBUTOR],
      'create_group': [UserRole.FARM_ADMIN, UserRole.DISTRIBUTOR],
      'edit_group': [UserRole.FARM_ADMIN, UserRole.DISTRIBUTOR],
      'add_device': [UserRole.FARM_ADMIN, UserRole.DISTRIBUTOR,UserRole.BASIC],
      'stop_trial': [UserRole.FARM_ADMIN, UserRole.DISTRIBUTOR,UserRole.BASIC],
      'view_historicalData': [],
      'switch_account': [UserRole.VIVENT_ADMIN],
      'stop_device': [UserRole.FARM_ADMIN, UserRole.DISTRIBUTOR, UserRole.BASIC],
      'delete_device': [UserRole.DISTRIBUTOR, UserRole.FARM_ADMIN, UserRole.BASIC],
      'connect_device': [UserRole.FARM_ADMIN, UserRole.DISTRIBUTOR, UserRole.BASIC],
      'replace_device': [UserRole.FARM_ADMIN, UserRole.DISTRIBUTOR, UserRole.BASIC],
    };

    return actionPermissions[action]?.includes(this.currentRole) ?? false;
  }
}
